import { AppDispatch } from '@store/index'
import {
  setSyncStatus,
  setSyncSuccess,
  setSyncError,
  setPendingCount,
} from '@store/slices/offlineSlice'
import { addNotification } from '@store/slices/uiSlice'
import {
  getQueueItems,
  removeQueueItem,
  incrementRetryCount,
  OfflineQueueItem,
  STORES,
  putRecords,
  addConflict,
  addSyncHistory,
} from '@/lib/db/indexed-db'
import { notifyDroppedQueueItems } from './offlineWriteService'
import { api } from './api/api'

const MAX_RETRY_COUNT = 3

type ApiError = { response?: { status?: number; data?: { message?: string } } }

function isConflict(err: unknown): boolean {
  return (err as ApiError)?.response?.status === 409
}

const ENTITY_CONFIG: Record<
  OfflineQueueItem['entity'],
  { path: string; updateMethod: 'put' | 'patch' }
> = {
  cigar: { path: '/cigars', updateMethod: 'put' },
  session: { path: '/smoking-sessions', updateMethod: 'put' },
  humidor: { path: '/humidors', updateMethod: 'put' },
}

function stripOfflineMeta(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload
  const rest = { ...(payload as Record<string, unknown>) }
  delete rest.offlineId
  return rest
}

async function executeQueueItem(item: OfflineQueueItem): Promise<void> {
  const { operation, entity, payload } = item
  const config = ENTITY_CONFIG[entity]
  if (!config) return

  const { path, updateMethod } = config
  const body = operation === 'CREATE' ? stripOfflineMeta(payload) : payload

  if (operation === 'CREATE') {
    await api.post(path, body)
  } else if (operation === 'UPDATE') {
    const data = body as { id: string; [key: string]: unknown }
    const { id, ...updates } = data
    if (updateMethod === 'put') {
      await api.put(`${path}/${id}`, updates)
    } else {
      await api.patch(`${path}/${id}`, updates)
    }
  } else if (operation === 'DELETE') {
    const data = body as { id: string }
    await api.delete(`${path}/${data.id}`)
  }
}

// Module-level flag to prevent concurrent sync operations.
// Safe because the guard at the start of syncOfflineQueue ensures
// only one sync runs at a time; the finally block (line 156) always resets this flag.
let isSyncing = false

export async function syncOfflineQueue(dispatch: AppDispatch): Promise<void> {
  if (isSyncing) return
  isSyncing = true

  const startedAt = Date.now()

  try {
    const items = await getQueueItems()
    if (items.length === 0) {
      isSyncing = false
      return
    }

    dispatch(setSyncStatus('syncing'))

    let synced = 0
    let failed = 0
    let conflicts = 0
    let dropped = 0

    for (const item of items) {
      if (item.retryCount >= MAX_RETRY_COUNT) {
        await removeQueueItem(item.id)
        dropped++
        failed++
        continue
      }

      try {
        await executeQueueItem(item)
        await removeQueueItem(item.id)
        synced++
      } catch (err) {
        if (isConflict(err)) {
          const apiErr = err as ApiError
          await addConflict({
            queueItemId: item.id,
            operation: item.operation,
            entity: item.entity,
            localPayload: item.payload,
            serverError: apiErr.response?.data?.message ?? 'Conflict with server version',
            timestamp: Date.now(),
            resolved: false,
          })
          await removeQueueItem(item.id)
          conflicts++
        } else {
          await incrementRetryCount(item.id)
          failed++
        }
      }
    }

    if (dropped > 0) {
      notifyDroppedQueueItems(dispatch, dropped)
    }

    await addSyncHistory({
      timestamp: Date.now(),
      itemsSynced: synced,
      itemsFailed: failed,
      conflicts,
      durationMs: Date.now() - startedAt,
    })

    const remaining = await getQueueItems()
    dispatch(setPendingCount(remaining.length))

    if (conflicts > 0) {
      const msg = `${conflicts} sync conflict${conflicts !== 1 ? 's' : ''} detected. Review conflicts to resolve.`
      dispatch(setSyncError(msg))
      dispatch(addNotification({ type: 'warning', title: 'Sync conflicts', message: msg }))
    } else if (failed > 0 && remaining.length > 0) {
      const msg = 'Some changes could not be synced. Will retry automatically.'
      dispatch(setSyncError(msg))
      dispatch(addNotification({ type: 'error', title: 'Sync failed', message: msg }))
    } else {
      dispatch(setSyncSuccess())
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Sync failed'
    dispatch(setSyncError(message))
    dispatch(addNotification({ type: 'error', title: 'Sync failed', message }))
  } finally {
    isSyncing = false
  }
}

export async function cacheCollectionData(cigars: unknown[], sessions: unknown[]): Promise<void> {
  await putRecords(STORES.CIGARS, cigars as Array<{ id: string }>)
  await putRecords(STORES.SESSIONS, sessions as Array<{ id: string }>)
}
